import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Trophy, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useCheckinStreak } from '@/hooks/useCheckinStreak';
import { Skeleton } from '@/components/ui/skeleton';
import { ContextualNotificationPrompt } from '@/components/notifications/ContextualNotificationPrompt';

interface StreakDisplayProps {
  variant?: 'compact' | 'full';
  showNotificationPrompt?: boolean;
}

export function StreakDisplay({ variant = 'compact', showNotificationPrompt = false }: StreakDisplayProps) {
  const { streak, longestStreak, totalCheckins, isLoading } = useCheckinStreak();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="space-y-3">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-secondary/10 to-accent/10 border border-secondary/20"
        >
          <motion.div
            animate={streak > 0 ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 0.5, repeat: streak > 2 ? Infinity : 0, repeatDelay: 3 }}
            className="p-2 rounded-full bg-secondary/20"
          >
            <Flame className="h-5 w-5 text-secondary" />
          </motion.div>
          
          <div className="flex-1">
            <p className="font-bold text-lg text-secondary">
              {streak} Day{streak !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-muted-foreground">
              {streak === 0 
                ? 'Start your streak today!' 
                : streak === 1 
                  ? 'Great start! Keep going!'
                  : `${streak} days in a row!`
              }
            </p>
          </div>

          {longestStreak > streak && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Best</p>
              <p className="font-semibold text-sm">{longestStreak}</p>
            </div>
          )}
        </motion.div>

        {showNotificationPrompt && streak > 0 && (
          <ContextualNotificationPrompt context="streak" contextValue={streak} />
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-1 min-w-0"
          >
            <motion.div
              animate={streak > 0 ? { scale: [1, 1.15, 1] } : {}}
              transition={{ duration: 0.6, repeat: streak > 2 ? Infinity : 0, repeatDelay: 4 }}
              className="mx-auto p-2 rounded-full bg-secondary/20 w-fit"
            >
              <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-secondary" />
            </motion.div>
            <p className="text-xl sm:text-2xl font-bold text-secondary">{streak}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Current Streak</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-1 min-w-0"
          >
            <div className="mx-auto p-2 rounded-full bg-accent/20 w-fit">
              <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-accent">{longestStreak}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Best Streak</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-1 min-w-0"
          >
            <div className="mx-auto p-2 rounded-full bg-primary/20 w-fit">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-primary">{totalCheckins}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Total Check-ins</p>
          </motion.div>
        </div>

        {showNotificationPrompt && streak > 0 && (
          <ContextualNotificationPrompt context="streak" contextValue={streak} />
        )}
      </CardContent>
    </Card>
  );
}
